import { FILTERS_NAME } from '@va/constants';
import { useTrafficChannelValues } from '@va/dashboard/api-client/traffic-channels';
import { useTranslate } from '@va/localization';
import { BooleanFilter, MultiSelectFilter, SingleSelectFilter } from '@va/shared/feature-filters';
import { FilterLogicalOperators, FilterOperators } from '@va/types/filters';
import { useMemo } from 'react';
import { FilterHook } from '../types';

const ALL_TRAFFIC_CHANNEL_VALUES = 'ALL_TRAFFIC_CHANNEL_VALUES';

export const useNewDirectChannelFilter: FilterHook<typeof SingleSelectFilter> = (params) => {
  const translate = useTranslate();
  return useMemo(() => {
    return new SingleSelectFilter({
      id: 'traffic-channel-direct',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_DIRECT,
      apiFieldNameMapper: () => 'trafficChannel',
      label: translate('filters.trafficChannel.direct'),
      operatorOptions: [FilterOperators.is, FilterOperators.isNot],
      valueMapper: (value) => {
        return [{ type: 'direct', category: value[0] === ALL_TRAFFIC_CHANNEL_VALUES ? [] : value }];
      },
      ...params,
      inputProps: {
        useOptions: () => ({ data: [{ value: ALL_TRAFFIC_CHANNEL_VALUES, label: translate('filters.labels.ALL') }] }),
        ...params?.inputProps,
      },
    });
  }, [params, translate]);
};

export const useNewSocialChannelFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(() => {
    return new MultiSelectFilter({
      id: 'traffic-channel-social',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_SOCIAL,
      apiFieldNameMapper: () => 'trafficChannel',
      label: translate('filters.trafficChannel.socialMedia'),
      operatorOptions: [FilterOperators.is, FilterOperators.isNot],
      optionsLogicalOperator: FilterLogicalOperators.or,
      valueMapper: (value) => {
        return [{ type: 'social', category: value[0] === ALL_TRAFFIC_CHANNEL_VALUES ? [] : value }];
      },
      ...params,
      inputProps: {
        useOptions: () => useTrafficChannelValues('social'),
        allOption: { value: ALL_TRAFFIC_CHANNEL_VALUES, label: translate('filters.labels.ALL'), allOption: true },
        showSearchInput: true,
        ...params?.inputProps,
      },
    });
  }, [params, translate]);
};

export const useNewOrganicChannelFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(() => {
    return new MultiSelectFilter({
      id: 'traffic-channel-search',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_SEARCH,
      apiFieldNameMapper: () => 'trafficChannel',
      label: translate('filters.trafficChannel.organicSearch'),
      operatorOptions: [FilterOperators.is, FilterOperators.isNot],
      optionsLogicalOperator: FilterLogicalOperators.or,
      valueMapper: (value) => {
        return [{ type: 'search', category: value[0] === ALL_TRAFFIC_CHANNEL_VALUES ? [] : value }];
      },
      ...params,
      inputProps: {
        useOptions: () => useTrafficChannelValues('search'),
        allOption: { value: ALL_TRAFFIC_CHANNEL_VALUES, label: translate('filters.labels.ALL'), allOption: true },
        showSearchInput: true,
        ...params?.inputProps,
      },
    });
  }, [params, translate]);
};

export const useNewReferrerChannelFilter: FilterHook<typeof BooleanFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(() => {
    return new BooleanFilter({
      id: 'traffic-channel-referrals',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_REFERRALS,
      label: translate('filters.trafficChannel.referrals'),
      apiFieldNameMapper: () => 'trafficChannel',
      valueMapper: (value, _operator) => {
        if (value) {
          return [{ type: 'unknown', category: [] }];
        }

        return [];
      },
      ...params,
    });
  }, [params, translate]);
};

export const useNewPaidChannelFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(() => {
    return new MultiSelectFilter({
      id: 'traffic-channel-paid',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_PAID,
      label: translate('filters.trafficChannel.paid'),
      operatorOptions: [FilterOperators.is, FilterOperators.isNot],
      optionsLogicalOperator: FilterLogicalOperators.or,
      valueMapper: (value) => {
        return [{ type: 'paid', category: value[0] === ALL_TRAFFIC_CHANNEL_VALUES ? [] : value }];
      },
      apiFieldNameMapper: () => 'trafficChannel',
      ...params,
      inputProps: {
        useOptions: () => useTrafficChannelValues('paid'),
        allOption: { value: ALL_TRAFFIC_CHANNEL_VALUES, label: translate('filters.labels.ALL'), allOption: true },
        showSearchInput: true,
        ...params?.inputProps,
      },
    });
  }, [params, translate]);
};

export const useNewEmailChannelFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(() => {
    return new MultiSelectFilter({
      id: 'traffic-channel-email',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_EMAIL,
      apiFieldNameMapper: () => 'trafficChannel',
      label: translate('filters.trafficChannel.email'),
      operatorOptions: [FilterOperators.is, FilterOperators.isNot],
      optionsLogicalOperator: FilterLogicalOperators.or,
      valueMapper: (value) => {
        return [{ type: 'email', category: value[0] === ALL_TRAFFIC_CHANNEL_VALUES ? [] : value }];
      },
      ...params,
      inputProps: {
        useOptions: () => useTrafficChannelValues('email'),
        allOption: { value: ALL_TRAFFIC_CHANNEL_VALUES, label: translate('filters.labels.ALL'), allOption: true },
        showSearchInput: true,
        ...params?.inputProps,
      },
    });
  }, [params, translate]);
};

export const useAiChannelFilter: FilterHook<typeof MultiSelectFilter> = (params) => {
  const translate = useTranslate();

  return useMemo(() => {
    return new MultiSelectFilter({
      id: 'traffic-channel-ai',
      name: FILTERS_NAME.TRAFFIC_CHANNEL_AI,
      apiFieldNameMapper: () => 'trafficChannel',
      label: translate('filters.trafficChannel.ai'),
      operatorOptions: [FilterOperators.is, FilterOperators.isNot],
      optionsLogicalOperator: FilterLogicalOperators.or,
      valueMapper: (value) => {
        return [{ type: 'ai_traffic', category: value[0] === ALL_TRAFFIC_CHANNEL_VALUES ? [] : value }];
      },
      ...params,
      inputProps: {
        useOptions: () => useTrafficChannelValues('ai_traffic'),
        allOption: { value: ALL_TRAFFIC_CHANNEL_VALUES, label: translate('filters.labels.ALL'), allOption: true },
        showSearchInput: true,
        ...params?.inputProps,
      },
    });
  }, [params, translate]);
};
